'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { usePathname, useRouter } from 'next/navigation';
import { Check, X } from 'lucide-react';
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  isValidLocale,
  type Locale,
} from '@/shared/config/locales';
import { useI18n } from '@/shared/providers/I18nProvider';
import { cn } from '@/lib/utils';

function setLocaleCookie(locale: Locale) {
  // 1 year
  document.cookie = `locale=${locale}; path=/; max-age=31536000; samesite=lax`;
}

function replaceLocaleInPath(pathname: string, nextLocale: Locale): string {
  const path = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const parts = path.split('/');
  const maybeLocale = parts[1];
  if (maybeLocale && isValidLocale(maybeLocale)) {
    parts[1] = nextLocale;
    return parts.join('/') || `/${nextLocale}`;
  }
  return `/${nextLocale}${path}`;
}

export function LanguageSwitcherSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const router = useRouter();
  const pathname = usePathname() || '/';
  const { lang, dict } = useI18n();

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const sheet = (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label={dict.chat.header.languageAria}
        onClick={onClose}
      />

      <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-lg rounded-t-3xl bg-card p-4 shadow-2xl">
        <div className="flex items-center justify-between pb-2">
          <div className="text-foreground text-base font-semibold">
            {dict.chat.languageSheet.title}
          </div>
          <button
            type="button"
            className="hover:bg-secondary/80 rounded-full p-2 transition-colors"
            aria-label={dict.common.close}
            onClick={onClose}
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        <div className="mt-2 grid gap-1">
          {SUPPORTED_LOCALES.map((locale) => {
            const active = locale === lang;
            return (
              <button
                key={locale}
                type="button"
                onClick={() => {
                  setLocaleCookie(locale);
                  router.push(replaceLocaleInPath(pathname, locale));
                  onClose();
                }}
                className={cn(
                  'flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left transition-colors',
                  active
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-secondary/60 text-foreground',
                )}
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {LOCALE_LABELS[locale]}
                  </span>
                  <span className="text-muted-foreground text-xs">{locale}</span>
                </div>
                {active ? <Check className="h-5 w-5" /> : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(sheet, document.body)
    : null;
}

